import { useState } from 'react';
import { Button, Group, Stack, Text, TextInput, Title, ThemeIcon } from '@mantine/core';
import { TrendingUp, Brain, BarChart3 } from 'lucide-react';
import { useMailingList } from '@/hooks/useMailingList';

function EmailSignup() {
  const [email, setEmail] = useState('');
  const { mutate, isPending, isSuccess, error, reset } = useMailingList();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    mutate(email.trim(), {
      onSuccess: () => setEmail(''),
    });
  }

  if (isSuccess) {
    return (
      <Stack gap="xs" align="center">
        <Text fw={600} c="teal" size="lg">
          You're on the list!
        </Text>
        <Text c="dimmed" size="sm">
          We'll email you when TickerBuzz launches.
        </Text>
        <Button variant="subtle" size="xs" onClick={reset}>
          Register another email
        </Button>
      </Stack>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="sm" align="center">
        <Group gap="sm" wrap="nowrap" className="w-full" style={{ maxWidth: 420 }}>
          <TextInput
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            aria-label="Email address"
            error={error?.message}
            flex={1}
            required
          />
          <Button type="submit" loading={isPending} style={{ flexShrink: 0 }}>
            Notify me
          </Button>
        </Group>
        {error && (
          <Text size="xs" c="red">
            {error.message}
          </Text>
        )}
      </Stack>
    </form>
  );
}

const FEATURES = [
  {
    icon: TrendingUp,
    title: 'Real-time Buzz Score',
    body: 'A 0–100 signal computed every 15 minutes from news volume, relevance, recency, and polarity.',
  },
  {
    icon: BarChart3,
    title: 'Top 100 NYSE',
    body: 'The largest US-listed companies by market cap, with live prices and daily market cap refresh.',
  },
  {
    icon: Brain,
    title: 'AI-powered summaries',
    body: "Click any ticker for an AI-generated summary of what's driving its buzz, with source links.",
  },
];

export function HomePage() {
  return (
    <div className="page-content home-page">
      {/* Parallax hero */}
      <section className="hero-section" aria-label="Coming soon">
        {/* Decorative parallax blobs — aria-hidden */}
        <div className="parallax-blob parallax-blob-1" aria-hidden="true" />
        <div className="parallax-blob parallax-blob-2" aria-hidden="true" />
        <div className="parallax-blob parallax-blob-3" aria-hidden="true" />

        <Stack gap="xl" align="center" className="hero-content">
          <Stack gap="md" align="center">
            <Title
              order={1}
              ta="center"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 4rem)', letterSpacing: '-0.02em' }}
            >
              Coming Soon
            </Title>
            <Text
              ta="center"
              c="dimmed"
              size="lg"
              maw={520}
              style={{ lineHeight: 1.65 }}
            >
              TickerBuzz ranks NYSE-listed companies by a real-time{' '}
              <strong>Buzz Score</strong> — a signal built from news volume,
              regulatory activity, and market momentum. Be the first to know
              when we launch.
            </Text>
          </Stack>

          <EmailSignup />
        </Stack>
      </section>

      {/* Below-fold features — gives the page scroll range for parallax */}
      <section className="features-section" aria-label="Features">
        <Stack gap="xl" align="center">
          <Title order={2} size="h3" ta="center">
            What's coming
          </Title>
          <div className="features-grid">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <Stack key={title} gap="sm" className="feature-card">
                <ThemeIcon size="lg" variant="light" radius="md">
                  <Icon size={18} aria-hidden />
                </ThemeIcon>
                <Text fw={600}>{title}</Text>
                <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
                  {body}
                </Text>
              </Stack>
            ))}
          </div>
        </Stack>
      </section>
    </div>
  );
}
